// src/components/social/posts/AdoptionPostEmbed.jsx
"use client";

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { adoptionAPI } from '../../../services/adoptionAPI';
import { getUserPlaceholderImage } from '../../../utils/imageUtils';
import { getBreedName, normalizePetInfo } from '../../../utils/petDataUtils';
import { showSuccessToast, showErrorToast } from '../../../utils/notificationUtils';
import { handleApiError } from '../../../services/api';
import MultiMediaDisplay from './MultiMediaDisplay';
import '../../modals/AdoptionPostDetailModal.css';

const AdoptionPostEmbed = ({ adoptionPostId, adoptionPost: initialAdoptionPost = null, currentUser, onAdoptionRequestSent }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18nInstance.dir() === "rtl";
  const [adoptionPost, setAdoptionPost] = useState(initialAdoptionPost);
  const [loading, setLoading] = useState(!initialAdoptionPost && !!adoptionPostId);
  const [isOffering, setIsOffering] = useState(false);
  const [activePetIndex, setActivePetIndex] = useState(0);

  useEffect(() => {
    // If we already have the adoption post data, use it
    if (initialAdoptionPost) {
      setAdoptionPost(initialAdoptionPost);
      setLoading(false);
      return;
    }

    // Otherwise, fetch it
    if (!adoptionPostId) {
      setLoading(false);
      return;
    }

    const fetchAdoptionPost = async () => {
      try {
        setLoading(true);
        const response = await adoptionAPI.getAdoptionPost(adoptionPostId);
        
        let data = null;
        if (response?.data) {
          if (response.data.status === "success" && response.data.data) {
            data = response.data.data;
          } else if (response.data.data) {
            data = response.data.data;
          } else {
            data = response.data;
          }
        }
        
        setAdoptionPost(data);
      } catch (error) {
        // 404 is expected when the adoption post was deleted — skip logging
        if (error?.response?.status !== 404) {
          console.error('Error fetching adoption post:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAdoptionPost();
  }, [adoptionPostId, initialAdoptionPost]);

  // Reset active pet index when post changes
  useEffect(() => {
    if (!adoptionPost) return;
    
    const petInfo = adoptionPost?.pet_info
      ? Array.isArray(adoptionPost.pet_info)
        ? adoptionPost.pet_info
        : [adoptionPost.pet_info]
      : Array.isArray(adoptionPost?.pets)
      ? adoptionPost.pets
      : adoptionPost?.pet
      ? [adoptionPost.pet]
      : [];

    if (petInfo.length > 0) {
      setActivePetIndex(0);
    }
  }, [adoptionPost?.id]);

  if (loading) {
    return (
      <div className="adoption-post-detail-modal-loading" style={{ padding: "24px", textAlign: "center" }}>
        <div className="adoption-post-detail-modal-spinner" />
        <p>{t('adoption.embed.loading') || 'Loading adoption post...'}</p>
      </div>
    );
  }

  if (!adoptionPost) {
    return (
      <div className="adoption-post-detail-modal-error" style={{ padding: "24px", textAlign: "center" }}>
        <p>{t('adoption.embed.notFound') || 'Adoption post not found.'} {adoptionPostId ? `(ID: ${adoptionPostId})` : ''}</p>
      </div>
    );
  }

  // Derive pet info
  const petInfo = normalizePetInfo(
    adoptionPost?.pet_info || adoptionPost?.pets || adoptionPost?.pet || []
  );

  const hasMultiplePets = petInfo.length > 1;
  const activePet = petInfo[activePetIndex] || petInfo[0] || {};
  const isAdopted = (adoptionPost?.status || "").toLowerCase() === "adopted";
  
  // Get the adoption post owner ID from multiple possible locations
  const adoptionPostOwnerId = adoptionPost?.author?.id || 
                               adoptionPost?.author?.user_id ||
                               adoptionPost?.user_id ||
                               adoptionPost?.user?.id ||
                               adoptionPost?.owner_id ||
                               null;
  
  // Get current user ID from multiple possible locations
  const currentUserId = currentUser?.id || 
                        currentUser?.user_id ||
                        null;
  
  // More robust owner check - compare adoption post owner (not the social post sharer)
  const isOwner = currentUserId && adoptionPostOwnerId && 
                  String(currentUserId) === String(adoptionPostOwnerId);
  
  const myRequestStatus = adoptionPost?.my_request_status || null;

  // Build media list for the active pet
  const getMediaForPet = (pet) => {
    const media = [];
    const primaryImage =
      pet.picture ||
      pet.profile_image_url ||
      (typeof pet.profile_image_path === "string"
        ? pet.profile_image_path
        : pet.profile_image_path?.url || pet.profile_image_path?.path) ||
      pet.image ||
      pet.avatar;

    if (primaryImage) {
      media.push(primaryImage);
    }

    // Add additional images if they exist
    if (Array.isArray(adoptionPost?.additional_images)) {
      adoptionPost.additional_images.forEach((img) => {
        const url =
          typeof img === "string" ? img : img?.url || img?.path || null;
        if (url && url !== primaryImage) {
          media.push(url);
        }
      });
    }

    return media;
  };

  const media = getMediaForPet(activePet);

  // Helper function to get pet details
  const getPetDetails = (pet) => {
    const ageLabel = pet.calculated_age || (() => {
      const raw = pet.birthdate || pet.birth_date;
      if (!raw) return t("adoption.post.pet.notSpecified") || "Not specified";
      const birth = new Date(raw);
      const today = new Date();
      let years = today.getFullYear() - birth.getFullYear();
      let months = today.getMonth() - birth.getMonth();
      if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
        years--;
        months += 12;
      }
      if (years <= 0) {
        return `${months} ${t("adoption.post.pet.months") || "months"}`;
      }
      if (months === 0) {
        return `${years} ${t("adoption.post.pet.years") || "years"}`;
      }
      return `${years}y ${months}m`;
    })();

    const gender = pet.gender || t("adoption.post.pet.notSpecified") || "Not specified";
    const breed = getBreedName(pet.breed) || t("adoption.post.pet.notSpecified") || "Not specified";
    const weight = pet.weight_kg || pet.weight_kg_fmt || pet.weight;

    const neuteredFlag = pet.is_neutered ?? pet.neutered ?? pet.spayed_neutered ?? null;
    const vaccinatedFlag = adoptionPost?.vaccinated ?? pet.is_vaccinated ?? pet.vaccinated ?? null;

    const neuteredLabel =
      neuteredFlag === null
        ? t("adoption.post.pet.notSpecified") || "Not specified"
        : neuteredFlag
        ? t("common.yes", "Yes")
        : t("common.no", "No");

    const vaccinatedLabel =
      vaccinatedFlag === null
        ? t("adoption.post.pet.notSpecified") || "Not specified"
        : vaccinatedFlag
        ? t("common.yes", "Yes")
        : t("common.no", "No");

    return {
      ageLabel,
      gender,
      breed,
      weight,
      neuteredFlag,
      vaccinatedFlag,
      neuteredLabel,
      vaccinatedLabel,
    };
  };

  const petDetails = getPetDetails(activePet);
  const petName = activePet.name || t('adoption.post.pet.unknownName') || 'Unnamed pet';

  const handleOfferToAdopt = async () => {
    if (!adoptionPost?.id || isOffering) return;
    setIsOffering(true);
    try {
      await adoptionAPI.sendAdoptionRequest(adoptionPost.id);
      showSuccessToast(
        t("adoption.postFeed.offerSuccess") ||
          "Your offer to adopt has been sent successfully!"
      );
      // Update local state
      setAdoptionPost((prev) =>
        prev
          ? {
              ...prev,
              my_request_status: "pending",
              adoption_requests_count: (prev.adoption_requests_count || 0) + 1,
            }
          : prev
      );
      if (onAdoptionRequestSent) {
        onAdoptionRequestSent(adoptionPost.id);
      }
    } catch (err) {
      const message =
        handleApiError(err) ||
        t("adoption.postFeed.offerError") ||
        "Failed to send adoption offer";
      showErrorToast(message);
    } finally {
      setIsOffering(false);
    }
  };

  const handleViewProfile = (e) => {
    if (!adoptionPost?.author?.id) return;
    e.preventDefault();
    e.stopPropagation();
    // Open profile in a new tab to avoid navigating away from current page
    const profileUrl = `/profile/${adoptionPost.author.id}`;
    window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  const createdAt = adoptionPost?.created_at || adoptionPost?.createdAt || null;
  const createdAtLabel = createdAt
    ? new Date(createdAt).toLocaleDateString(
        i18nInstance.language === "ar" ? "ar-SA" : "en-US",
        { year: "numeric", month: "long", day: "numeric" }
      )
    : null;

  const ownerName = adoptionPost?.author?.name || adoptionPost?.author?.username || "";

  const title = hasMultiplePets
    ? t("adoption.details.multiplePetsTitle", { count: petInfo.length }) ||
      `${petInfo.length} Pets for Adoption`
    : ownerName
    ? t("adoption.details.titleWithName", { name: activePet.name || "" }) ||
      `${activePet.name || t("adoption.page.title")}`
    : activePet.name || t("adoption.page.title") || "Pet adoption";

  return (
    <div className="adoption-detail-layout" style={{ position: "relative", marginTop: "16px", border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden", background: "#FFFFFF", padding: "20px 24px 24px" }}>
      {isAdopted && (
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <div style={{
            background: "rgba(0, 0, 0, 0.7)",
            color: "#FFFFFF",
            padding: "12px 24px",
            borderRadius: "20px",
            fontWeight: "600",
            fontSize: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            textAlign: "center",
            maxWidth: "80%"
          }}>
            {t("adoption.embed.adoptedOverlay") || "This pet has been given a caring home"}
          </div>
        </div>
      )}

      <div className="adoption-detail-media" key={`pet-media-${activePetIndex}`}>
        {media.length > 0 ? (
          <MultiMediaDisplay media={media} />
        ) : (
          <div className="adoption-detail-media-placeholder">
            <img
              src="/images/no-image-pet.png"
              alt={activePet.name || "Pet"}
            />
          </div>
        )}
      </div>

      <div className="adoption-detail-info">
        {/* Owner */}
        <div className="adoption-detail-owner">
          <div className="adoption-detail-owner-main">
            <div className="adoption-detail-owner-avatar">
              <img
                src={
                  adoptionPost?.author?.avatar ||
                  adoptionPost?.author?.profile_image_url ||
                  getUserPlaceholderImage(adoptionPost?.author)
                }
                alt={ownerName || "Owner"}
                onError={(e) => {
                  e.target.src = getUserPlaceholderImage(adoptionPost?.author);
                }}
              />
            </div>
            <div className="adoption-detail-owner-text">
              <span className="adoption-detail-owner-label">
                {t("adoption.post.owner") || "Owner"}
              </span>
              <span className="adoption-detail-owner-name">
                {ownerName}
              </span>
            </div>
          </div>
                  {adoptionPost?.author?.id && (
                    <a
                      href={`/profile/${adoptionPost.author.id}`}
                      className="adoption-detail-owner-button"
                      onClick={handleViewProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("adoption.post.viewProfile") || "View profile"}
                    </a>
                  )}
        </div>

        {/* Pet tabs (if multiple pets) */}
        {hasMultiplePets && (
          <div className="adoption-detail-pet-tabs">
            {petInfo.map((pet, index) => (
              <button
                key={pet.id || index}
                type="button"
                className={`adoption-detail-pet-tab ${
                  activePetIndex === index ? "active" : ""
                }`}
                onClick={() => setActivePetIndex(index)}
              >
                {pet.name || t("adoption.modals.selectPet.unknownPet") || `Pet ${index + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* Pet details */}
        <div className="adoption-detail-section">
          <h3 className="adoption-detail-section-title">
            {activePet.name || t("adoption.post.pet.details") || "Pet details"}
          </h3>
          <div className="adoption-detail-grid">
            <div className="adoption-detail-item">
              <div className="adoption-detail-item-label">
                {t("adoption.post.pet.age") || "Age"}
              </div>
              <div className="adoption-detail-item-value">{petDetails.ageLabel}</div>
            </div>
            <div className="adoption-detail-item">
              <div className="adoption-detail-item-label">
                {t("adoption.post.pet.gender") || "Gender"}
              </div>
              <div className="adoption-detail-item-value">{petDetails.gender}</div>
            </div>
            <div className="adoption-detail-item">
              <div className="adoption-detail-item-label">
                {t("adoption.post.pet.breed") || "Breed"}
              </div>
              <div className="adoption-detail-item-value">{petDetails.breed}</div>
            </div>
            <div className="adoption-detail-item">
              <div className="adoption-detail-item-label">
                {t("adoption.post.pet.weight") || "Weight"}
              </div>
              <div className="adoption-detail-item-value">
                {petDetails.weight
                  ? `${petDetails.weight} kg`
                  : t("adoption.post.pet.notSpecified") ||
                    "Not specified"}
              </div>
            </div>
            <div className="adoption-detail-item">
              <div className="adoption-detail-item-label">
                {t("adoption.post.pet.neutered") || "Neutered / spayed"}
              </div>
              <span
                className={`adoption-detail-pill ${
                  petDetails.neuteredFlag
                    ? "adoption-detail-pill--yes"
                    : petDetails.neuteredFlag === false
                    ? "adoption-detail-pill--no"
                    : ""
                }`}
              >
                {petDetails.neuteredLabel}
              </span>
            </div>
            <div className="adoption-detail-item">
              <div className="adoption-detail-item-label">
                {t("adoption.post.pet.vaccinated") || "Vaccinated"}
              </div>
              <span
                className={`adoption-detail-pill ${
                  petDetails.vaccinatedFlag
                    ? "adoption-detail-pill--yes"
                    : petDetails.vaccinatedFlag === false
                    ? "adoption-detail-pill--no"
                    : ""
                }`}
              >
                {petDetails.vaccinatedLabel}
              </span>
            </div>
          </div>
        </div>

        {/* About / Looking for */}
        {adoptionPost?.about_pet && (
          <div className="adoption-detail-section">
            <h4 className="adoption-detail-section-title">
              {t("adoption.post.about") || "About"}
            </h4>
            <p className="adoption-detail-text">{adoptionPost.about_pet}</p>
          </div>
        )}

        {adoptionPost?.looking_for && (
          <div className="adoption-detail-section">
            <h4 className="adoption-detail-section-title">
              {t("adoption.post.lookingFor") || "What we are looking for"}
            </h4>
            <p className="adoption-detail-text">{adoptionPost.looking_for}</p>
          </div>
        )}

        {!isOwner && !isAdopted && (
          <button
            type="button"
            className="adoption-detail-offer-btn"
            onClick={handleOfferToAdopt}
            disabled={
              isOffering || myRequestStatus === "pending"
            }
          >
            {isOffering
              ? t("adoption.post.offerSubmitting") || "Submitting..."
              : myRequestStatus === "pending"
              ? t("adoption.post.pending") || "Pending"
              : t("adoption.post.offerToAdopt") ||
                "Offer to Adopt"}
          </button>
        )}
      </div>

      {/* Safety disclaimer - spans entire width */}
      <div className="adoption-detail-section adoption-detail-disclaimer">
        <h4 className="adoption-detail-section-title">
          {t("adoption.details.disclaimerTitle") || "Safety disclaimer"}
        </h4>
        <p className="adoption-detail-text adoption-detail-disclaimer-text">
          {t("adoption.details.disclaimerBody") ||
            "Sulhafa is a community platform and does not independently verify the identity, information, or intentions of users. Always proceed with caution when arranging adoption offers, independently confirm all details about the pet and the other party, and avoid sharing sensitive personal or financial information."}
        </p>
      </div>

      {/* Footer - spans entire width */}
      <div className="adoption-detail-footer">
        <div className="adoption-detail-footer-meta">
          {createdAtLabel && (
            <span className="adoption-post-date">
              {createdAtLabel}
            </span>
          )}
          {adoptionPost?.adoption_requests_count > 0 && (
            <span className="adoption-detail-requests">
              {adoptionPost.adoption_requests_count}{" "}
              {t("adoption.post.requests") || "request(s)"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdoptionPostEmbed;
